// src/services/coachPosts.js
// Firestore helpers for Danielle's Corner (coach posts on Dashboard)
// Collection: coachPosts
// Schema: { body, imageUrl, imageSize, createdBy, createdAt, updatedAt, status }
// imageSize: 'small' | 'medium' | 'large' (2026-10-06). Absent ⇒ medium —
// see normalizePostImageSize in components/CoachPostImage.js.

import { db, firebaseEnabled } from './firebase';
import { normalizePostImageSize } from '../components/CoachPostImage';
import {
    collection, getDocs, doc, query, where, orderBy, limit as firestoreLimit,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    addDoc, updateDoc,
} from './firestoreWrite';

// ── Create a new coach post ──
export async function createCoachPost({ body, imageUrl, imageSize, createdBy }) {
    if (!firebaseEnabled || !db || !body?.trim()) return null;
    try {
        const post = {
            body: body.trim().slice(0, 500),
            imageUrl: imageUrl?.trim() || '',
            imageSize: normalizePostImageSize(imageSize),
            createdBy,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            status: 'active',
        };
        const docRef = await addDoc(collection(db, 'coachPosts'), post);
        return { id: docRef.id, ...post };
    } catch (e) {
        console.error('Error creating coach post:', e);
        return null;
    }
}

// ── Fetch recent active posts (for Dashboard display) ──
export async function getRecentCoachPosts(maxPosts = 3) {
    if (!firebaseEnabled || !db) return [];
    try {
        const q = query(
            collection(db, 'coachPosts'),
            where('status', '==', 'active'),
            orderBy('createdAt', 'desc'),
            firestoreLimit(maxPosts)
        );
        const snap = await getDocs(q);
        // Client-side filter: only show posts from the last 30 days
        const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
        return snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((p) => new Date(p.createdAt).getTime() > cutoff);
    } catch (e) {
        console.error('Error fetching coach posts:', e);
        return [];
    }
}

// ── Fetch all posts (for admin management) ──
export async function getAllCoachPosts() {
    if (!firebaseEnabled || !db) return [];
    try {
        const q = query(
            collection(db, 'coachPosts'),
            orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
        console.error('Error fetching all coach posts:', e);
        return [];
    }
}

// ── Update a coach post ──
export async function updateCoachPost(postId, updates) {
    if (!firebaseEnabled || !db || !postId) return false;
    try {
        const safeUpdates = {};
        if (updates.body !== undefined) safeUpdates.body = updates.body.trim().slice(0, 500);
        if (updates.imageUrl !== undefined) safeUpdates.imageUrl = updates.imageUrl?.trim() || '';
        if (updates.imageSize !== undefined) safeUpdates.imageSize = normalizePostImageSize(updates.imageSize);
        if (updates.status !== undefined) safeUpdates.status = updates.status;
        safeUpdates.updatedAt = new Date().toISOString();
        await updateDoc(doc(db, 'coachPosts', postId), safeUpdates);
        return true;
    } catch (e) {
        console.error('Error updating coach post:', e);
        return false;
    }
}

// ── Soft-delete a coach post ──
export async function deleteCoachPost(postId) {
    if (!firebaseEnabled || !db || !postId) return false;
    try {
        await updateDoc(doc(db, 'coachPosts', postId), {
            status: 'deleted',
            updatedAt: new Date().toISOString(),
        });
        return true;
    } catch (e) {
        console.error('Error deleting coach post:', e);
        return false;
    }
}
